import React, { createContext, useContext } from "react";

/**
 * App-supplied wiring for the editable Plan Option header cell (CD-1261).
 *
 * The popover UI lives in the shared lib (EditablePlanOptionHeader), but the
 * save path, dropdown options, custom-text overrides, and per-plan editability
 * are all app concerns (Quotes tab). The app provides them here, mirroring the
 * CellViewRendererOverride context pattern, so the lib stays app-agnostic.
 */
export interface PlanOptionEditorApi {
  /** Selectable option values with display labels (e.g. Low/Medium/High). */
  options: { value: string; label: string }[];
  /** Persist a new enum option for a plan. */
  onSelectOption: (planId: string, optionName: string) => void;
  /** Persist (or clear, when null) free-text override for a plan's option. */
  onCustomTextChange: (planId: string, customText: string | null) => void;
  /** Current custom-text override for a plan, if any. */
  getCustomText: (planId: string) => string | undefined;
  /**
   * Live enum-option label for a plan (from the current grid data). Read here
   * rather than from a static column param so an edit reflects after refetch,
   * since AG Grid does not refresh header-group component params in place.
   */
  getOptionLabel: (planId: string) => string;
  /**
   * Live enum-option value for a plan (e.g. "low"), used to preselect the
   * dropdown when opening the editor on an already-populated cell.
   */
  getOptionValue: (planId: string) => string;
  /** False for read-only columns (e.g. the Incumbent/Requested carrier). */
  isEditable: (planId: string) => boolean;
  /** True once the analyst has edited this plan's option (drives the pencil). */
  isEdited: (planId: string) => boolean;
  /** Flag this plan's option as edited (reuses the cell-review edited flag). */
  markEdited: (planId: string) => void;
}

const PlanOptionEditorContext = createContext<PlanOptionEditorApi | null>(null);

interface PlanOptionEditorProviderProps {
  api: PlanOptionEditorApi;
  children: React.ReactNode;
}

export const PlanOptionEditorProvider = ({
  api,
  children,
}: PlanOptionEditorProviderProps): React.JSX.Element => (
  <PlanOptionEditorContext.Provider value={api}>
    {children}
  </PlanOptionEditorContext.Provider>
);

export const usePlanOptionEditor = (): PlanOptionEditorApi | null =>
  useContext(PlanOptionEditorContext);
